// src/api/goalsApi.ts

import { baseApi } from './baseApi';
import type {
  Goal,
  GoalsResponse,
  CreateGoalRequest,
  UpdateGoalRequest,
} from '../types/api';

export interface ListGoalsParams {
  workspaceId?: string;
  status?: string;
  periodType?: string;
  ownerUserId?: string;
  activeOn?: string;
  limit?: number;
  offset?: number;
}

export const goalsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getGoals: builder.query<GoalsResponse, ListGoalsParams>({
      query: (params) => ({
        url: '/goals',
        params,
      }),
      providesTags: (result, _err, params) => [
        ...(result?.goals ?? []).map(({ id }) => ({ type: 'Goal' as const, id })),
        { type: 'Goal', id: params.workspaceId ?? 'LIST' },
      ],
    }),

    getGoal: builder.query<Goal, string>({
      query: (id) => `/goals/${id}`,
      providesTags: (_result, _err, id) => [{ type: 'Goal', id }],
    }),

    createGoal: builder.mutation<Goal, CreateGoalRequest>({
      query: (body) => ({ url: '/goals', method: 'POST', body }),
      invalidatesTags: (_result, _err, { workspaceId }) => [
        { type: 'Goal', id: workspaceId ?? 'LIST' },
      ],
    }),

    updateGoal: builder.mutation<Goal, { id: string } & UpdateGoalRequest>({
      query: ({ id, ...body }) => ({ url: `/goals/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _err, { id }) => [{ type: 'Goal', id }],
    }),

    archiveGoal: builder.mutation<{ message: string; id: string }, string>({
      query: (id) => ({ url: `/goals/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _err, id) => [{ type: 'Goal', id }],
    }),
  }),
});

export const {
  useGetGoalsQuery,
  useGetGoalQuery,
  useCreateGoalMutation,
  useUpdateGoalMutation,
  useArchiveGoalMutation,
} = goalsApi;
